/**
 * @fileoverview Provides a `DocumentationContextProvider` that uses `@fileoverview` comments to select relevant files for a given prompt. It uses an LLM to choose files based on their overviews, as read by `readAllFileOverviews`, and returns their content. The `FileOverview` type represents the path and overview of a file.
 */

import { serializeForLlm } from '@iterator/shared-common/src/serialize_for_llm'
import { existsSync, readFileSync, statSync } from 'fs'
import path, * as pathlib from 'path'
import { GoogleWithCaching } from '@iterator/shared-node/ai/google'
import { ContextProvider, ContextProviderFn, FolderContext } from './context_types'
import { FILE_OVERVIEW_REGEX } from './document'
import { getFilesMatchingNames } from './filename_context'
import { walkFolder } from './folder'
import { debug_block, error, isVerbose, print_ai, warn } from '@iterator/shared-node/util/logging'
import { AiStreamer } from './ai_streamer'
import { AwolfSettings } from './settings'
import { AWOLF_DIRECTORY_NAME } from './awolf_dir'
import {
  baseOfSparsePath,
  hashSparsePath,
  SparsePath,
} from '@iterator/shared-node/util/sparse_path'

/**
 * This context provider reads the `@fileoverview` comments in files and decides which relevant to the prompt.
 */
export class DocumentationContextProvider implements ContextProvider {
  shouldThink(): boolean {
    return false
  }

  getContext: ContextProviderFn = async (
    directory: SparsePath,
    prompt?: string,
  ): Promise<FolderContext> => {
    if (!prompt) {
      throw new Error('Prompt is required for documentation context provider')
    }

    // Make sure cache accounts for the directory and the subdirs
    const cacheHash = hashSparsePath(directory)
    const api = new GoogleWithCaching(
      AwolfSettings.singleton(),
      path.join(process.cwd(), AWOLF_DIRECTORY_NAME, cacheHash),
    )

    const overviews = await readAllFileOverviews(directory)
    const overviewPrompt = `Overviews: ${overviews.map((o) => `${o.path}: ${o.overview}`).join('\n')}`
    debug_block('overviews', overviewPrompt)
    const aiStreamer = new AiStreamer()

    const { cacheHit } = await api.cacheContent(
      SYSTEM_PROMPT,
      overviewPrompt,
      'documentationContext',
    )
    print_ai(cacheHit ? '✅ Cache hit' : '🔄 Cache miss, item created')

    const { message } = await api.streamChatCompletions(
      undefined,
      [],
      [
        {
          role: 'user',
          content: `<user_prompt_to_pick_files_for>${prompt}</user_prompt_to_pick_files_for>`,
        },
      ],
      (content) => {
        if (!isVerbose()) {
          aiStreamer.stream(content)
        }
      },
      undefined,
      'documentationContext',
    )

    debug_block('Documentation context', message.content)
    const system = `Relevant files:\n${message.content}`
    const filesToRead = message.content
      .split('\n')
      .filter((path) => existsSync(path))
      .map((path) => pathlib.join(baseOfSparsePath(directory), path))
    debug_block('Files chosen based on overview', filesToRead)

    const filesMentioned = getFilesMatchingNames(prompt, directory)
    debug_block('Files chosen based on name', filesMentioned.matching)
    for (const file of filesMentioned.matching) {
      if (!filesToRead.includes(file)) {
        filesToRead.push(file)
        if (!isVerbose()) {
          for (const file of filesToRead) {
            print_ai(`${file}`)
          }
        }
      }
    }

    return {
      system,
      texts: Array.from(new Set(filesToRead))
        .slice(0, 50)
        .filter((path) => existsSync(path) && statSync(path).isFile())
        .map((path) => serializeForLlm.file({ path, contents: readFileSync(path, 'utf8') })),
    }
  }
}

export type FileOverview = {
  path: string
  overview: string
}
export const NO_OVERVIEW_FOUND = '(none provided)'

/**
 * Logs a warning when files have no overview.
 */
export function checkIfOverviewsAreEmpty(overviews: FileOverview[]) {
  const fractionWithNoOverview =
    overviews.filter((overview) => overview.overview === NO_OVERVIEW_FOUND).length /
    overviews.length
  if (fractionWithNoOverview > 0.95) {
    error(
      `Greater than 95% of files have no overview ${overviews.length} files. Run 'awolf document' to generate overviews.`,
    )
  } else if (fractionWithNoOverview > 0.5) {
    warn(
      `Greater than 50% of files have no overview ${overviews.length} files. Run 'awolf document' to generate overviews.`,
    )
  }
}

/**
 * Reads all the `@fileoverview` comments in a directory and returns the path and overview for each file.
 */
export async function readAllFileOverviews(directory: SparsePath): Promise<FileOverview[]> {
  const overviews: FileOverview[] = []

  walkFolder(directory, (path, type) => {
    if (type === 'file') {
      const contents = readFileSync(path, 'utf8')
      const match = contents.match(FILE_OVERVIEW_REGEX)
      if (match) {
        overviews.push({
          path: pathlib.relative(baseOfSparsePath(directory), path),
          overview: match[1]!.trim(),
        })
      } else {
        overviews.push({
          path: pathlib.relative(baseOfSparsePath(directory), path),
          overview: NO_OVERVIEW_FOUND,
        })
      }
    }
  })

  return overviews
}

const SYSTEM_PROMPT = `You are a software engineer who is deciding which files are important to read based on overviews.

You will be provided with a list of files and their overviews. You will need to decide which files are most relevant to the prompt.

Output a list of files paths that are most relevant to the prompt. Include the most relevant files first. Output one paths per line and nothing else.
`
